import { ZOOMAPP_CTX_PARAM } from '@lib/zoom/constants';

import { makeAuthCopy } from './copy';
import { PLATFORMS, PLATFORM_DISPLAY_NAME_TO_KEY, type PlatformSlug } from './registry';
import type { PlatformAuth, PlatformAuthCopy, PlatformMeta } from './types';

/** The Next.js proxy path that fronts the backend's `/auth/zoom` route. */
export const ZOOM_PROXY_AUTH_PATH = '/api/auth/zoom';

/**
 * Keys `Presentation/constants.ts` uses that don't match a registry slug. It calls
 * the catch-all platform `other`; the registry (and the backend) call it
 * `other-online-events`.
 */
const SLUG_ALIASES: Record<string, PlatformSlug> = {
  other: 'other-online-events',
};

/**
 * Callers identify platforms two different ways — the Presentation forms pass
 * display names ("Zoom"), the settings pages pass slugs ("zoom") — so every
 * lookup here accepts either rather than forcing call sites to convert.
 */
export const resolvePlatformKey = (platform?: string): PlatformSlug | undefined => {
  if (!platform) return undefined;
  if (platform in PLATFORMS) return platform as PlatformSlug;
  if (platform in SLUG_ALIASES) return SLUG_ALIASES[platform];
  const fromDisplayName = PLATFORM_DISPLAY_NAME_TO_KEY[platform];
  return (fromDisplayName as PlatformSlug) || undefined;
};

export const getPlatform = (platform?: string): PlatformMeta | undefined => {
  const key = resolvePlatformKey(platform);
  return key ? PLATFORMS[key] : undefined;
};

export const getPlatformAuth = (platform?: string): PlatformAuth | undefined =>
  getPlatform(platform)?.auth;

/** Every platform the backend can OAuth against, in display order. */
export const OAUTH_PLATFORMS: PlatformMeta[] = Object.values(PLATFORMS)
  .filter((entry): entry is PlatformMeta & { auth: PlatformAuth } => !!entry.auth)
  .sort((a, b) => a.order - b.order);

/** The platforms that get a row in the settings / integrations lists. */
export const getSettingsPlatforms = (section?: PlatformAuth['settingsSection']) =>
  OAUTH_PLATFORMS.filter(
    (entry) =>
      entry.auth!.showInSettings &&
      (!section || entry.auth!.settingsSection === section)
  );

/**
 * Authorize copy for any platform. Falls back to generic copy for a platform with
 * no `auth` block: this is display text, and a missing entry must never blank the
 * screen.
 */
export const getPlatformAuthCopy = (platform?: string): PlatformAuthCopy => {
  const entry = getPlatform(platform);
  if (entry?.auth) return entry.auth.copy;
  return makeAuthCopy(entry?.title || platform || 'this platform');
};

export interface AuthorizeLinkOptions {
  /** teamContext.oid — nullable because the team context hydrates asynchronously. */
  userId?: string | null;
  /**
   * Only `/auth/zoom` reads this, and only accepts `web|zoomapp`. Omitting it
   * server-side means `zoomapp`, so browser surfaces must pass `web`.
   */
  appType?: 'web' | 'zoomapp';
  /** Zoom re-link: tells the OAuth callback this isn't a first authorization. */
  relink?: boolean;
  /** Zoom App context id, forwarded through the OAuth state. */
  saCtx?: string | null;
  /** Defaults to the API origin; pass '/api/auth/' to go through the Next proxy. */
  base?: string;
}

const defaultBase = () => `${process.env.NEXT_PUBLIC_API_URL}auth/`;

/**
 * The one authorize-URL builder. Returns undefined for a platform with no backend
 * auth route, so a link-entry or extension-only platform can't produce a dead URL.
 */
export const getAuthorizeLink = (
  platform?: string,
  { userId, appType = 'web', relink, saCtx, base }: AuthorizeLinkOptions = {}
): string | undefined => {
  const auth = getPlatformAuth(platform);
  if (!auth) return undefined;

  const params = new URLSearchParams({ id: String(userId) });
  if (auth.sendsAppType) params.set('appType', appType);
  if (relink) params.set('relink', '1');
  if (saCtx) params.set(ZOOMAPP_CTX_PARAM, saCtx);

  return `${base ?? defaultBase()}${auth.authPath}?${params.toString()}`;
};

/**
 * Authorize URLs keyed by display name, the shape the Presentation forms index
 * into (`authorizeLink[selectedPlatform]`). Built from the registry so a new
 * OAuth platform shows up without touching the hooks.
 */
export const getAuthorizeLinks = (userId?: string | null): Record<string, string> =>
  Object.fromEntries(
    OAUTH_PLATFORMS.map((entry) => [
      entry.title,
      getAuthorizeLink(entry.slug, { userId })!,
    ])
  );

/**
 * Stamps the standard authorize params onto an existing URL, for the Zoom re-link
 * flows that start from the current location and have to keep its other params.
 */
export const applyAuthorizeParams = (
  url: URL,
  { userId, appType = 'web', relink, saCtx }: AuthorizeLinkOptions = {}
): URL => {
  url.searchParams.set('id', String(userId));
  url.searchParams.set('appType', appType);
  if (relink) url.searchParams.set('relink', '1');
  if (saCtx) url.searchParams.set(ZOOMAPP_CTX_PARAM, saCtx);
  return url;
};

/**
 * The deauthorize payload for a platform. Zoom is the only one where revoking is
 * meaningful — the backend's revoke branch is hard-scoped to it — and it must
 * behave the same whether the user disconnects from Settings or from the form.
 */
export const getDeauthorizePayload = (platform?: string) => {
  const auth = getPlatformAuth(platform);
  return auth?.supportsRevoke ? { revoke: true } : {};
};
